import dayjs from 'dayjs';
import { gql, useQuery } from '@apollo/react-hooks';
import type { GsDecision, GetGsDecision, FileCheck } from './types';
import { GS_DECISION } from '../../GSAssessment';
import { type ApplicationRemarkType } from '../../../Nomination/graphql';
import { type SupersedeData } from './GSChecklist/useGenerateOfferFiles';

export const useGetGsAssessment = (applicationId: string): GetGsDecision => {
  const { data, loading, error, refetch } = useQuery(
    gql`
      query ($applicationId: String!, $id: ID!) {
        previewOfferFiles(applicationId: $applicationId) {
          programmeCodeSubcode
          studyCommencementDate
          hasFellowship
          fellowshipSchemeFullname
          fulltimeLocal
          selfFinanced
          hasAssisstantship
          assisstantShipFullOrHalf
          residenceRequirements
        }

        application(id: $id) {
          programmeChoice {
            department
          }
          remarks {
            fromRole
            toRole
            remark
            createdAt
          }
        }
        gsDecision(applicationId: $applicationId) {
          applicationId
          assignee
          decision
          allItemChecked
          fileChecks {
            file {
              id
              link
              name
            }
            checked
          }
          firstCheckedBy
          firstCheckedAt
          secondCheckedBy
          secondCheckedAt
          remarksToSupervisor
          remarks
        }
      }
    `,
    {
      variables: { applicationId, id: applicationId },
      skip: !applicationId,
      fetchPolicy: 'network-only',
    },
  );

  const gsDecisionData = data?.gsDecision ?? {};
  const hadGsChecked = gsDecisionData?.allItemChecked ?? false;

  const {
    fileChecks,
    decision,
    assignee,
    firstCheckedBy,
    secondCheckedBy,
    firstCheckedAt,
    secondCheckedAt,
    // TODO `remarksToSupervisor` to be replaced by `remarks`,
    remarksToSupervisor,
  } = gsDecisionData;

  const gsDecision: GsDecision = {
    decision: (decision as string)?.includes('RETURN')
      ? GS_DECISION.RETURN
      : decision,
    assignee,
    gsDecision: (decision as string)?.includes('RETURN')
      ? (decision as GS_DECISION)
      : undefined,
    remarks: remarksToSupervisor ?? '',
    fileChecks: (fileChecks as FileCheck[]) ?? [],
    firstCheckedBy: firstCheckedBy ?? '',
    firstCheckedAt:
      firstCheckedAt !== undefined && firstCheckedAt !== null
        ? dayjs(firstCheckedAt).format('DD/MM/YYYY')
        : '',
    secondCheckedBy: secondCheckedBy ?? '',
    secondCheckedAt:
      secondCheckedAt !== undefined && secondCheckedAt !== null
        ? dayjs(secondCheckedAt).format('DD/MM/YYYY')
        : '',
  };

  const remarks: ApplicationRemarkType[] = data?.application?.remarks ?? [];

  // previewOfferFiles might be null

  const studyCommencementDate =
    data?.previewOfferFiles?.studyCommencementDate ?? '';

  const previewData: SupersedeData = {
    programmeCodeSubcode: data?.previewOfferFiles?.programmeCodeSubcode ?? '',
    studyCommencementDate:
      studyCommencementDate !== ''
        ? dayjs(studyCommencementDate).format('DD/MM/YYYY')
        : null,

    hasFellowship: data?.previewOfferFiles?.hasFellowship ?? false,
    fellowshipSchemeFullname:
      data?.previewOfferFiles?.fellowshipSchemeFullname ?? '',
    fulltimeLocal: data?.previewOfferFiles?.fulltimeLocal ?? false,
    selfFinanced: data?.previewOfferFiles?.selfFinanced ?? false,
    hasAssisstantship: data?.previewOfferFiles?.hasAssisstantship ?? false,
    assisstantShipFullOrHalf:
      data?.previewOfferFiles?.assisstantShipFullOrHalf ?? '',
    residenceRequirements: data?.previewOfferFiles?.residenceRequirements ?? '',
  };

  const department = data?.application?.programmeChoice?.department ?? '';
  return {
    gsDecision,
    previewData,
    remarks,
    hadGsChecked,
    department,
    loading,
    error,
    refetch,
  };
};
